/**
 * Whose data this device holds.
 *
 * The outbox is one queue per DEVICE (D18–D23), and a session that ended
 * without the person choosing it — a refused refresh — leaves items pending.
 * Without an owner they replay under whoever signs in next, and a
 * `session.start` that never synced carries no server row to refuse it: that
 * workout would be created inside the new account. `@repo/client/offline`
 * stamps every item with an `accountId` and scopes its readers (T28); this is
 * the app's single answer to "which account is that".
 *
 * ── THE LISTENERS RUN ON A CHANGE, NEVER ON A SESSION ENDING ───────────────
 * {@link AccountScope.onLeave} is where T35 registers the per-account cleanup
 * (the active workout, check-in dismissals, composer drafts, search recents).
 * It runs on a deliberate {@link AccountScope.leave} and when a DIFFERENT
 * account {@link AccountScope.enter}s — never when a refresh fails and the
 * same person signs back in, whose workout must survive that.
 *
 * ── NATIVE-FREE ON PURPOSE ─────────────────────────────────────────────────
 * The storage is injected, so this runs in Node under `vitest`. `provider.tsx`
 * is the one file that binds Capacitor `Preferences` to it (the T11 split).
 */
import type { KeyValueStorage } from "@repo/client/i18n";

/** Capacitor Preferences key. The account whose data this device holds. */
export const ACCOUNT_STORAGE_KEY = "mypocketgym.account.current";

export interface AccountScope {
  /** Synchronous; `null` until {@link load} resolved, or when none is known. */
  current(): string | null;
  /**
   * A sign-in finished for `accountId`. When a DIFFERENT account was current,
   * every {@link onLeave} listener runs first, with the previous id, BEFORE
   * `current()` moves.
   *
   * `changed` says `current()` moved, which includes adopting an account where
   * there was none (the legacy claim below, where no listener runs).
   */
  enter(
    accountId: string
  ): Promise<{ readonly changed: boolean; readonly previous: string | null }>;
  /** A deliberate sign-out: runs the listeners, then forgets the account. */
  leave(): Promise<void>;
  /** Read the stored account once. Idempotent; a failed read is retried. */
  load(): Promise<void>;
  /**
   * Called with the account being LEFT, before `current()` moves. Returns the
   * unsubscribe. A listener that throws is logged and the others still run:
   * losing a cleanup must never strand the account itself.
   */
  onLeave(listener: (previous: string) => Promise<void> | void): () => void;
  /** Told whenever `current()` would answer differently. */
  subscribe(listener: () => void): () => void;
}

export function createAccountScope(storage: KeyValueStorage): AccountScope {
  let account: string | null = null;
  let loading: Promise<void> | null = null;
  /** `enter` or `leave` has spoken; a late `load` must not overrule it. */
  let settled = false;
  const changeListeners = new Set<() => void>();
  const leaveListeners = new Set<(previous: string) => Promise<void> | void>();

  const announce = () => {
    for (const listener of [...changeListeners]) {
      listener();
    }
  };

  const runLeaveListeners = async (previous: string): Promise<void> => {
    for (const listener of [...leaveListeners]) {
      try {
        await listener(previous);
      } catch (error) {
        console.warn("[data] account cleanup failed", error);
      }
    }
  };

  /**
   * A failed write is logged, never thrown. The in-memory account is what this
   * run stamps and drains under; the stored copy only decides the NEXT boot.
   * Rejecting here would fail a sign-in whose tokens are already stored.
   */
  const write = async (value: string | null): Promise<void> => {
    try {
      if (value === null) {
        await storage.removeItem(ACCOUNT_STORAGE_KEY);
      } else {
        await storage.setItem(ACCOUNT_STORAGE_KEY, value);
      }
    } catch (error) {
      console.warn("[data] account could not be stored", error);
    }
  };

  return {
    current: () => account,

    async enter(accountId) {
      settled = true;
      const previous = account;
      if (previous === accountId) {
        return { changed: false, previous };
      }
      if (previous !== null) {
        await runLeaveListeners(previous);
      }
      account = accountId;
      await write(accountId);
      announce();
      return { changed: true, previous };
    },

    async leave() {
      settled = true;
      const previous = account;
      if (previous !== null) {
        await runLeaveListeners(previous);
      }
      account = null;
      await write(null);
      announce();
    },

    load() {
      loading ??= (async () => {
        try {
          const stored = await storage.getItem(ACCOUNT_STORAGE_KEY);
          // A sign-in during the read wins: it is the newer fact.
          if (!settled && typeof stored === "string" && stored.length > 0) {
            account = stored;
            announce();
          }
        } catch (error) {
          loading = null;
          console.warn("[data] account could not be read", error);
        }
      })();
      return loading;
    },

    onLeave(listener) {
      leaveListeners.add(listener);
      return () => {
        leaveListeners.delete(listener);
      };
    },

    subscribe(listener) {
      changeListeners.add(listener);
      return () => {
        changeListeners.delete(listener);
      };
    },
  };
}
